import { useEffect } from 'react';
import {
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { FaApple as FaAppleIcon, FaWindows as FaWindowsIcon } from 'react-icons/fa';
const FaApple = FaAppleIcon as React.ComponentType<{ size?: number }>;
const FaWindows = FaWindowsIcon as React.ComponentType<{ size?: number }>;
import { AnimatePresence, motion } from 'motion/react';
import { useGoServerStore } from '@/stores/goServerStore';
import FigmaStepModal from '../../modal/FigmaStepModal';
import { ONBOARDING_STEP_LABELS, ONBOARDING_TOTAL_STEPS } from '../../../model/constants';

interface AudioSetupStepProps {
  setupStatus: {
    isUp: boolean;
    error: string | null;
    lastChecked: Date | string | null;
  };
  setupLoading: boolean;
  isSaving: boolean;
  onRefreshStatus: () => Promise<void>;
  onContinue: () => Promise<void>;
}

const INSTALLERS = [
  {
    label: 'Windows',
    subtitle: 'למחשבי Windows',
    href: '/installers/windows/CloserAudio-x64-Setup.msi',
    iconType: 'windows',
  },
  {
    label: 'macOS',
    subtitle: 'למחשבי Mac',
    href: '/installers/mac/CloserAudio-Setup.pkg',
    iconType: 'apple',
  },
] as const;

const INSTALL_VIDEOS = [
  {
    label: 'Windows',
    subtitle: 'סרטון התקנה למחשבי Windows',
    src: '/windows%20install.mp4',
    iconType: 'windows',
  },
  {
    label: 'macOS',
    subtitle: 'סרטון התקנה למחשבי Mac',
    src: '/mac%20install.mp4',
    iconType: 'apple',
  },
] as const;

const AudioSetupStep: React.FC<AudioSetupStepProps> = ({
  isSaving,
  onRefreshStatus,
  onContinue,
}) => {
  const {
    connectionStatus,
    startPeriodicConnectionCheck,
    checkConnection,
  } = useGoServerStore();
  const isServerConnected = connectionStatus.isConnected;

  useEffect(() => {
    void checkConnection();
    const stop = startPeriodicConnectionCheck();
    return () => stop();
  }, [checkConnection, startPeriodicConnectionCheck]);

  useEffect(() => {
    if (!isServerConnected) {
      return;
    }

    void onRefreshStatus();
  }, [isServerConnected, onRefreshStatus]);

  const isDisabled = isSaving || !isServerConnected;
  const primaryAction = (
    <button
      type="button"
      onClick={() => void onContinue()}
      disabled={isDisabled}
      aria-disabled={isDisabled}
      title={!isServerConnected ? 'יש להתקין ולהפעיל את Closer.AI כדי להמשיך' : undefined}
      className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold text-white transition ${
        !isServerConnected
          ? 'cursor-not-allowed bg-brand-500 opacity-40 shadow-none'
          : 'bg-gradient-to-l from-brand-400 to-brand-700 shadow-[0px_16px_20px_-8px_rgba(173,51,10,0.25)] hover:brightness-105'
      } disabled:cursor-not-allowed`}
    >
      <span>{isSaving ? 'יאללה מתקדמים' : 'המשך לשלב הבא'}</span>
      <ArrowLeftIcon className="h-4 w-4" />
    </button>
  );

  return (
    <FigmaStepModal
      embedded
      stepNumber={1}
      totalSteps={ONBOARDING_TOTAL_STEPS}
      stepLabels={[...ONBOARDING_STEP_LABELS]}
      title="התקנת Closer.AI"
      description="כדי להפעיל את המערכת יש לבצע התקנה קצרה  – כך קלוזר תוכל להאזין לשיחות המכירה ותיתן הנחיות בזמן אמת שייצרו יותר סגירות!  צפו בוידאו כדי לראות איך מתקינים את המערכת בקלות (מתחת לסרטון מחכה קובץ ההתקנה):"
      showBadge={false}
      titleBelow
      footerPrimary={primaryAction}
    >
      <div className="flex flex-col gap-4">
        {/* Platform-specific install videos */}
        <div className="grid gap-4 sm:grid-cols-2">
          {INSTALL_VIDEOS.map((videoItem, index) => (
            <motion.div
              key={videoItem.label}
              initial={{ opacity: 0, y: 14, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.32, delay: 0.06 + index * 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="overflow-hidden rounded-[24px] border border-neutral-200 bg-white shadow-[0px_18px_36px_-28px_rgba(25,28,30,0.3)] dark:border-neutral-700 dark:bg-neutral-800"
            >
              <div className="flex items-center justify-between border-b border-neutral-200 px-4 py-3 dark:border-neutral-700">
                <div className="inline-flex items-center gap-2">
                  <span
                    className={`flex h-9 w-9 items-center justify-center rounded-xl ${
                      videoItem.iconType === 'windows'
                        ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'
                        : 'bg-neutral-200 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-200'
                    }`}
                  >
                    {videoItem.iconType === 'windows' ? <FaWindows size={18} /> : <FaApple size={18} />}
                  </span>
                  <div className="text-right">
                    <p className="text-xs font-bold uppercase tracking-[0.22em] text-brand-700 dark:text-brand-300">
                      {videoItem.label}
                    </p>
                    <p className="text-xs text-text-700 dark:text-neutral-300">{videoItem.subtitle}</p>
                  </div>
                </div>
              </div>
              <div className="h-[220px] sm:h-[240px]">
                <video className="h-full w-full" controls preload="metadata">
                  <source src={videoItem.src} type="video/mp4" />
                  הדפדפן שלך לא תומך בנגן וידאו.
                </video>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Download cards */}
        <div className="grid gap-4 sm:grid-cols-2">
          {INSTALLERS.map((installer, index) => (
            <motion.a
              key={installer.label}
              href={installer.href}
              download
              initial={{ opacity: 0, y: 14, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.32, delay: 0.24 + index * 0.1, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ y: -4, scale: 1.01, transition: { duration: 0.18, ease: [0.22, 1, 0.36, 1] } }}
              whileTap={{ scale: 0.97, transition: { duration: 0.1 } }}
              className="group rounded-[24px] border border-neutral-200 bg-white px-6 py-5 text-right shadow-[0px_18px_36px_-28px_rgba(25,28,30,0.3)] hover:border-brand-200 hover:shadow-[0px_20px_40px_-24px_rgba(173,51,10,0.18)] dark:border-neutral-700 dark:bg-neutral-800 dark:hover:border-brand-700"
            >
              <div className="flex items-start justify-between gap-4">
                <div
                  className={`flex h-12 w-12 items-center justify-center rounded-2xl transition ${
                    installer.iconType === 'windows'
                      ? 'bg-blue-100 text-blue-600 group-hover:bg-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:group-hover:bg-blue-900/35'
                      : 'bg-neutral-200 text-neutral-800 group-hover:bg-neutral-300 dark:bg-neutral-700 dark:text-neutral-200 dark:group-hover:bg-neutral-600'
                  }`}
                >
                  {installer.iconType === 'windows' ? (
                    <span className="flex items-center justify-center"><FaWindows size={22} /></span>
                  ) : (
                    <span className="flex items-center justify-center"><FaApple size={22} /></span>
                  )}
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-brand-700 dark:text-brand-300">
                    {installer.label}
                  </p>
                  <p className="mt-2 text-sm leading-6 text-text-700 dark:text-neutral-300">{installer.subtitle}</p>
                  <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand-50 px-3 py-1.5 text-sm font-bold text-brand-700 dark:bg-brand-900/20 dark:text-brand-300">
                    <ArrowDownTrayIcon className="h-4 w-4" />
                    <span>Download</span>
                  </div>
                </div>
              </div>
            </motion.a>
          ))}
        </div>

        {/* Connection status section */}
        <div className="flex flex-col gap-3">
          {/* Status banner — yellow when disconnected, green when connected */}
          <AnimatePresence mode="wait" initial={false}>
            {isServerConnected ? (
              <motion.div
                key="connected-banner"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                className="inline-flex items-center gap-2 rounded-full border border-success-200 bg-success-50 px-4 py-2 text-sm font-bold text-success-700 dark:border-success-800 dark:bg-success-900/20 dark:text-success-300"
              >
                <CheckCircleIcon className="h-4 w-4" />
                <span>Closer.AI מחובר - ופועל ברקע</span>
              </motion.div>
            ) : (
              <motion.div
                key="disconnected-banner"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                className="flex flex-col gap-3"
              >
                <div className="inline-flex items-center gap-2 rounded-full border border-warning-200 bg-warning-50 px-4 py-2 text-sm font-bold text-warning-700 dark:border-warning-800 dark:bg-warning-900/20 dark:text-warning-300">
                  <motion.span
                    className="h-2.5 w-2.5 rounded-full bg-warning-500"
                    animate={{ scale: [1, 1.35, 1], opacity: [0.85, 1, 0.85] }}
                    transition={{ duration: 1.8, repeat: Number.POSITIVE_INFINITY, ease: 'easeInOut' }}
                  />
                  <span>Closer.AI עדיין לא הותקן/לא פועל ברקע</span>
                </div>
                <p className="text-sm leading-6 text-text-700 dark:text-neutral-300">
                  הפעילו את Closer.AI לאחר ההתקנה. כשהתוכנה תפעל ברקע היא תתחבר אוטומטית תוך מספר שניות והכפתור יהפוך לירוק.
                </p>

                {/* Help text — shown when server is not yet connected */}
                <a
                  href="https://calendly.com/closer-ai/support"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-base font-bold text-brand-700 hover:underline dark:text-brand-300"
                >
                  אם הכפתור לא הפך לירוק לאחר ההתקנה, לחצו כאן וקבעו שיחה עם הצוות הטכני &gt;&gt;
                </a>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </FigmaStepModal>
  );
};

export default AudioSetupStep;
